import { useRef, useEffect } from "react";
import { LayoutTemplate, Palette, Route, Share2, Wind } from "lucide-react";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { useFlowField, type FlowStartMode } from "./FlowFieldContext";
import type { FlowShapeMode } from "../../utils/flowField";
import {
    FLOW_ANGLE_NOISE_MODES,
    type FlowAngleNoiseMode,
} from "../../utils/flowAngleNoise";
import { getEffectiveDotMinGap } from "../../utils/flowFieldDots";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function FlowFieldControls() {
    const {
        width,
        height,
        marginFactor,
        cellSize,
        seed,
        angleNoiseMode,
        angleNoiseOctaves,
        angleWarpStrength,
        angleWarpFrequency,
        noiseScale,
        stepNoiseScale,
        stepVariation,
        angleQuantizeDeg,
        fieldRotationDeg,
        mirrorNoiseX,
        mirrorNoiseY,
        uniformFlowBlend,
        uniformFlowAngleDeg,
        flowShapeMode,
        setFlowShapeMode,
        flowShapeCircleRadiusScale,
        setFlowShapeCircleRadiusScale,
        bidirectional,
        stepLength,
        maxSteps,
        startMode,
        startSpacing,
        curveCount,
        strokeColor,
        strokeWidth,
        backgroundColor,
        exportBackground,
        showAngleDebug,
        drawDots,
        setDrawDots,
        dotMinGapExtra,
        setDotMinGapExtra,
        dotMinGapAtLeastStroke,
        setDotMinGapAtLeastStroke,
        pathDs,
        setWidth,
        setHeight,
        setMarginFactor,
        setCellSize,
        setSeed,
        setAngleNoiseMode,
        setAngleNoiseOctaves,
        setAngleWarpStrength,
        setAngleWarpFrequency,
        setNoiseScale,
        setStepNoiseScale,
        setStepVariation,
        setAngleQuantizeDeg,
        setFieldRotationDeg,
        setMirrorNoiseX,
        setMirrorNoiseY,
        setUniformFlowBlend,
        setUniformFlowAngleDeg,
        setBidirectional,
        setStepLength,
        setMaxSteps,
        setStartMode,
        setStartSpacing,
        setCurveCount,
        setStrokeColor,
        setStrokeWidth,
        setBackgroundColor,
        setExportBackground,
        setShowAngleDebug,
        randomizeParameters,
        canExport,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useFlowField();

    const strokeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const bgTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
            if (bgTimeout.current) clearTimeout(bgTimeout.current);
        };
    }, []);

    const handleStrokePicker = (value: string) => {
        if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
        strokeTimeout.current = setTimeout(() => setStrokeColor(value), 150);
    };

    const handleBgPicker = (value: string) => {
        if (bgTimeout.current) clearTimeout(bgTimeout.current);
        bgTimeout.current = setTimeout(() => setBackgroundColor(value), 150);
    };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    return (
        <div className="panel-controls blob-field-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Flow Field</h2>
                    <ToolDescriptionPopover dialogLabel="Flow Field tool description">
                        <p>
                            Angle grid from noise (Perlin / FBM / domain warp),
                            curves by stepping
                            along local direction - see{" "}
                            <a
                                href="https://www.tylerxhobbs.com/words/flow-fields"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Tyler Hobbs - Flow Fields
                            </a>
                            . Output is vector SVG (no HTML canvas).
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="flow-field"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup
                    title="Artboard"
                    icon={LayoutTemplate}
                    defaultOpen={true}
                >
                    <div className="field-row">
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={120}
                                max={2400}
                                step={10}
                                value={width}
                                onChange={handleInt(setWidth, 120)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Height
                            <DebouncedInput
                                type="number"
                                min={120}
                                max={2400}
                                step={10}
                                value={height}
                                onChange={handleInt(setHeight, 120)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Margin (x min side, extends field past artboard)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2}
                                step={0.05}
                                value={marginFactor}
                                onChange={handleFloat(setMarginFactor, 0, 2)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Grid cell size
                            <DebouncedInput
                                type="number"
                                min={4}
                                max={80}
                                step={1}
                                value={cellSize}
                                onChange={handleInt(setCellSize, 4)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Field"
                    icon={Wind}
                    defaultOpen={true}
                >
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Angle noise
                            <select
                                value={angleNoiseMode}
                                onChange={(e) =>
                                    setAngleNoiseMode(
                                        e.target.value as FlowAngleNoiseMode,
                                    )
                                }
                            >
                                {FLOW_ANGLE_NOISE_MODES.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {(angleNoiseMode === "fbm" ||
                        angleNoiseMode === "domain_warp") && (
                        <div className="field-row">
                            <label className="field-label">
                                FBM octaves (angle)
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    max={8}
                                    step={1}
                                    value={angleNoiseOctaves}
                                    onChange={handleInt(
                                        setAngleNoiseOctaves,
                                        1,
                                    )}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    )}
                    {angleNoiseMode === "domain_warp" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Warp strength (noise space)
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={2}
                                        step={0.05}
                                        value={angleWarpStrength}
                                        onChange={handleFloat(
                                            setAngleWarpStrength,
                                            0,
                                            2,
                                        )}
                                        debounceMs={400}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Warp frequency (x sampling coords)
                                    <DebouncedInput
                                        type="number"
                                        min={0.05}
                                        max={4}
                                        step={0.05}
                                        value={angleWarpFrequency}
                                        onChange={handleFloat(
                                            setAngleWarpFrequency,
                                            0.05,
                                            4,
                                        )}
                                        debounceMs={400}
                                    />
                                </label>
                            </div>
                        </>
                    )}
                    <div className="field-row">
                        <label className="field-label">
                            Noise scale (angle / grid sampling)
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={0.5}
                                step={0.01}
                                value={noiseScale}
                                onChange={handleFloat(setNoiseScale, 0.01, 0.5)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            2nd noise scale (step length)
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={0.5}
                                step={0.01}
                                value={stepNoiseScale}
                                onChange={handleFloat(
                                    setStepNoiseScale,
                                    0.01,
                                    0.5,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Step variation (2nd noise, 0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={stepVariation}
                                onChange={handleFloat(setStepVariation, 0, 1)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <p
                        className="control-panel-hint"
                        style={{ margin: "0 0 8px", fontSize: "0.75em" }}
                    >
                        Turn step variation above 0 to modulate step length with
                        a second Perlin field (independent seed). Angle noise
                        uses the main seed; step noise stays single-layer Perlin.
                    </p>
                    <div className="field-row">
                        <label className="field-label">
                            Field rotation ( deg)
                            <DebouncedInput
                                type="number"
                                min={-180}
                                max={180}
                                step={1}
                                value={fieldRotationDeg}
                                onChange={handleFloat(
                                    setFieldRotationDeg,
                                    -180,
                                    180,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={mirrorNoiseX}
                            onChange={setMirrorNoiseX}
                            label="Mirror noise horizontally"
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={mirrorNoiseY}
                            onChange={setMirrorNoiseY}
                            label="Mirror noise vertically"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Uniform flow blend (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={uniformFlowBlend}
                                onChange={handleFloat(
                                    setUniformFlowBlend,
                                    0,
                                    1,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Uniform flow angle ( deg)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={360}
                                step={1}
                                value={uniformFlowAngleDeg}
                                onChange={handleFloat(
                                    setUniformFlowAngleDeg,
                                    0,
                                    360,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <p
                        className="control-panel-hint"
                        style={{ margin: "0 0 8px", fontSize: "0.75em" }}
                    >
                        Rotate and mirror reshape the Perlin field; blend mixes
                        in a constant direction (0 deg = right).
                    </p>
                    <div className="field-row">
                        <label className="field-label">
                            Field shape
                            <select
                                value={flowShapeMode}
                                onChange={(e) =>
                                    setFlowShapeMode(
                                        e.target.value as FlowShapeMode,
                                    )
                                }
                                style={{ marginTop: "4px", width: "100%" }}
                            >
                                <option value="full">Full rectangle</option>
                                <option value="circle">Circle (artboard center)</option>
                            </select>
                        </label>
                    </div>
                    {flowShapeMode === "circle" && (
                        <div className="field-row">
                            <label className="field-label">
                                Circle radius (x inscribed)
                                <DebouncedInput
                                    type="number"
                                    min={0.02}
                                    max={1}
                                    step={0.02}
                                    value={flowShapeCircleRadiusScale}
                                    onChange={handleFloat(
                                        setFlowShapeCircleRadiusScale,
                                        0.02,
                                        1,
                                    )}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    )}
                    {flowShapeMode === "circle" && (
                        <p
                            className="control-panel-hint"
                            style={{ margin: "0 0 8px", fontSize: "0.75em" }}
                        >
                            Noise and curves use a polar mask from the artboard
                            center; radius is a fraction of the smaller
                            half-side (inscribed circle = 1).
                        </p>
                    )}
                    <div className="field-row">
                        <label className="field-label">
                            Quantize angles ( deg)
                            <select
                                value={String(angleQuantizeDeg)}
                                onChange={(e) =>
                                    setAngleQuantizeDeg(Number(e.target.value))
                                }
                                style={{ marginTop: "4px", width: "100%" }}
                            >
                                <option value="0">Off (smooth)</option>
                                <option value="5">5 deg</option>
                                <option value="10">10 deg</option>
                                <option value="15">15 deg</option>
                                <option value="22.5">22.5 deg</option>
                                <option value="30">30 deg</option>
                                <option value="45">45 deg</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Step length
                            <DebouncedInput
                                type="number"
                                min={0.5}
                                max={20}
                                step={0.5}
                                value={stepLength}
                                onChange={handleFloat(setStepLength, 0.5, 50)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max steps per curve
                            <DebouncedInput
                                type="number"
                                min={20}
                                max={2000}
                                step={10}
                                value={maxSteps}
                                onChange={handleInt(setMaxSteps, 20)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Starting points"
                    icon={Route}
                    defaultOpen={true}
                >
                    <div className="field-row">
                        <label className="field-label">
                            Mode
                            <select
                                value={startMode}
                                onChange={(e) =>
                                    setStartMode(
                                        e.target.value as FlowStartMode,
                                    )
                                }
                                style={{ marginTop: "4px", width: "100%" }}
                            >
                                <option value="grid">Regular grid</option>
                                <option value="random">Random</option>
                            </select>
                        </label>
                    </div>
                    {startMode === "grid" ? (
                        <div className="field-row">
                            <label className="field-label">
                                Grid spacing
                                <DebouncedInput
                                    type="number"
                                    min={4}
                                    max={120}
                                    step={1}
                                    value={startSpacing}
                                    onChange={handleInt(setStartSpacing, 4)}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    ) : (
                        <div className="field-row">
                            <label className="field-label">
                                Curve count
                                <DebouncedInput
                                    type="number"
                                    min={10}
                                    max={8000}
                                    step={50}
                                    value={curveCount}
                                    onChange={handleInt(setCurveCount, 10)}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    )}
                    <div className="field-row">
                        <CheckboxField
                            checked={bidirectional}
                            onChange={setBidirectional}
                            label="Bidirectional curves (trace forward and backward from each start)"
                            inline
                        />
                    </div>
                    <p className="control-panel-hint">
                        {pathDs.length} curves drawn
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Appearance"
                    icon={Palette}
                    defaultOpen={true}
                >
                    <div className="field-row">
                        <label className="field-label">
                            Stroke
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={strokeColor}
                                    onChange={(e) =>
                                        handleStrokePicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={strokeColor}
                                    onChange={(v) => setStrokeColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width
                            <DebouncedInput
                                type="number"
                                min={0.05}
                                max={4}
                                step={0.05}
                                value={strokeWidth}
                                onChange={handleFloat(setStrokeWidth, 0.05, 8)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={drawDots}
                            onChange={setDrawDots}
                            label="Dots at each step (instead of continuous strokes)"
                            inline
                        />
                    </div>
                    {drawDots && (
                        <>
                            <div className="field-row">
                                <CheckboxField
                                    checked={dotMinGapAtLeastStroke}
                                    onChange={setDotMinGapAtLeastStroke}
                                    label="Min spacing ≥ stroke width (reduces overlap)"
                                    inline
                                />
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Extra min gap between dot centers
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={200}
                                        step={0.5}
                                        value={dotMinGapExtra}
                                        onChange={handleFloat(
                                            setDotMinGapExtra,
                                            0,
                                            200,
                                        )}
                                        debounceMs={400}
                                    />
                                </label>
                            </div>
                            <p
                                className="control-panel-hint upload-info--tight-top"
                                style={{ marginBottom: "8px" }}
                            >
                                Effective min gap:{" "}
                                {getEffectiveDotMinGap(
                                    strokeWidth,
                                    dotMinGapExtra,
                                    dotMinGapAtLeastStroke,
                                ).toFixed(2)}{" "}
                                (SVG units, center-to-center along each curve)
                            </p>
                        </>
                    )}
                    <div className="field-row">
                        <label className="field-label">
                            Background
                            <div className="control-color-row">
                                <input
                                    type="color"
                                    value={backgroundColor}
                                    onChange={(e) =>
                                        handleBgPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={backgroundColor}
                                    onChange={(v) =>
                                        setBackgroundColor(String(v))
                                    }
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={exportBackground}
                            onChange={setExportBackground}
                            label="Include background in export"
                            inline
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={showAngleDebug}
                            onChange={setShowAngleDebug}
                            label="Show angle vectors (inside artboard)"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "flow-field-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["flow-field"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
